/**
 * The files the wizard hands back — their names, and the CSV half of their
 * contents: the empty template (§8.2) and the "Hatalı satırları indir" list the
 * owner fixes and re-uploads (§8.5). The XLSX half is `xlsx.ts`.
 *
 * File rules are Phase 1's (`DataTable.exportCsv`): semicolon, UTF-8 with BOM
 * and CRLF, so Turkish Excel opens the file with one double-click. Every cell
 * goes through `neutralizeFormula` first (§10.3) — the failing-rows file writes
 * the owner's own cells back, and quoting alone does not stop Excel evaluating a
 * cell that starts with `=`.
 */
import { BOM } from './parse';
import { neutralizeFormula } from './format';
import { templateHeaders, templateOf, type ImportSlug } from './fields';
import { errorRowMessage, type ImportRowResult } from './results';

export const CSV_DELIMITER = ';';

export type SpreadsheetExt = 'csv' | 'xlsx';

function cell(value: string): string {
  const v = neutralizeFormula(value);
  return /["\n\r;]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function toCsv(rows: string[][], delimiter: string = CSV_DELIMITER): string {
  return BOM + rows.map((r) => r.map((c) => cell(c ?? '')).join(delimiter)).join('\r\n') + '\r\n';
}

export function templateFileName(slug: ImportSlug, ext: SpreadsheetExt = 'csv'): string {
  return `${templateOf(slug).fileBase}.${ext}`;
}

/** Header row only; the XLSX template carries the help and reference sheets. */
export function buildTemplateCsv(slug: ImportSlug): string {
  return toCsv([templateHeaders(slug)]);
}

export function errorRowsFileBase(slug: ImportSlug, isoDate: string): string {
  return `${templateOf(slug).fileBase.replace('-sablonu-v1', '')}-hatali-satirlar-${isoDate}`;
}

export function errorRowsFileName(slug: ImportSlug, isoDate: string, ext: SpreadsheetExt = 'csv'): string {
  return `${errorRowsFileBase(slug, isoDate)}.${ext}`;
}

/**
 * The mapped cells of the failing rows with the messages in front of them. The
 * columns are the template's own, so deleting the first two columns leaves an
 * exact template file — the re-upload then skips the mapping step entirely.
 * Rows keep their file line number so they can be found in the source sheet.
 */
export function buildErrorRowsCsv(slug: ImportSlug, rows: ImportRowResult[]): string {
  const fields = templateOf(slug).fields;
  const head = ['Hata', 'Satır', ...templateHeaders(slug)];
  const body = rows.map((r) => [errorRowMessage(r), String(r.row_no), ...fields.map((f) => r.raw?.[f.key] ?? '')]);
  return toCsv([head, ...body]);
}
